import { keyframes, css } from 'styled-components'
import type { Effect } from '@/types'

// ═══════════════════════════════════════════════════════════════════════════
// INTRO/EXIT ANIMATION KEYFRAMES
// One-time animations for container entrance and exit
// ═══════════════════════════════════════════════════════════════════════════

export const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

export const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`

export const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
`

export const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
`

export const slideInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`

export const slideInDown = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`

export const slideOutLeft = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(-20px); }
`

export const slideOutRight = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(20px); }
`

export const slideOutUp = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-20px); }
`

export const slideOutDown = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(20px); }
`

export const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`

export const scaleOut = keyframes`
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.8); }
`

export const bounceIn = keyframes`
  0% { opacity: 0; transform: scale(0.3); }
  50% { opacity: 1; transform: scale(1.05); }
  70% { opacity: 1; transform: scale(0.9); }
  100% { opacity: 1; transform: scale(1); }
`

export const bounceOut = keyframes`
  0% { transform: scale(1); opacity: 1; }
  25% { transform: scale(1.05); }
  100% { transform: scale(0.3); opacity: 0; }
`

export const flipIn = keyframes`
  from { 
    opacity: 0; 
    transform: perspective(400px) rotateY(90deg);
  }
  to { 
    opacity: 1; 
    transform: perspective(400px) rotateY(0deg);
  }
`

export const flipOut = keyframes`
  from { 
    opacity: 1; 
    transform: perspective(400px) rotateY(0deg);
  }
  to { 
    opacity: 0; 
    transform: perspective(400px) rotateY(90deg);
  }
`

export const rotateIn = keyframes`
  from { 
    opacity: 0; 
    transform: rotate(-180deg) scale(0.5);
  }
  to { 
    opacity: 1; 
    transform: rotate(0deg) scale(1);
  }
`

export const rotateOut = keyframes`
  from { 
    opacity: 1; 
    transform: rotate(0deg) scale(1);
  }
  to { 
    opacity: 0; 
    transform: rotate(180deg) scale(0.5);
  }
`

export const blurIn = keyframes`
  from { 
    opacity: 0; 
    filter: blur(10px);
  }
  to { 
    opacity: 1; 
    filter: blur(0);
  }
`

export const blurOut = keyframes`
  from { 
    opacity: 1; 
    filter: blur(0);
  }
  to { 
    opacity: 0; 
    filter: blur(10px);
  }
`

export const elasticIn = keyframes`
  0% { opacity: 0; transform: scale(0); }
  55% { opacity: 1; transform: scale(1.15); }
  70% { opacity: 1; transform: scale(0.9); }
  85% { opacity: 1; transform: scale(1.05); }
  100% { opacity: 1; transform: scale(1); }
`

export const elasticOut = keyframes`
  0% { transform: scale(1); opacity: 1; }
  25% { transform: scale(1.1); }
  50% { transform: scale(0.9); opacity: 0.8; }
  100% { transform: scale(0); opacity: 0; }
`

export const dropIn = keyframes`
  0% { 
    opacity: 0; 
    transform: translateY(-100px);
  }
  60% { 
    opacity: 1; 
    transform: translateY(10px);
  }
  80% { 
    opacity: 1;
    transform: translateY(-5px);
  }
  100% { 
    opacity: 1;
    transform: translateY(0);
  }
`

export const dropOut = keyframes`
  0% { 
    opacity: 1; 
    transform: translateY(0);
  }
  100% { 
    opacity: 0; 
    transform: translateY(100px);
  }
`

// ═══════════════════════════════════════════════════════════════════════════
// ATTENTION EFFECT KEYFRAMES
// Continuous/looping animations to draw user attention
//
// For repeatDelay support, we use keyframes with a "pause" period at the end.
// The --active-pct CSS variable controls what percentage of the animation is active.
// Default is 50% (0.5) meaning animation happens in first half, pause in second half.
// Set to 1 (or don't set) for no pause between iterations.
// ═══════════════════════════════════════════════════════════════════════════

// Glow effect - uses box-shadow so can combine with transform effects
export const glowPulse = keyframes`
  0%, 100% { 
    box-shadow: 0 0 5px var(--glow-color, rgba(99, 102, 241, 0.3));
  }
  50% { 
    box-shadow: 0 0 20px var(--glow-color, rgba(99, 102, 241, 0.8)), 
                0 0 30px var(--glow-color, rgba(99, 102, 241, 0.4));
  }
`

// Glow with pause - animation in first half, holds in second half
export const glowPulseWithPause = keyframes`
  0% { 
    box-shadow: 0 0 5px var(--glow-color, rgba(99, 102, 241, 0.3));
  }
  25% { 
    box-shadow: 0 0 20px var(--glow-color, rgba(99, 102, 241, 0.8)), 
                0 0 30px var(--glow-color, rgba(99, 102, 241, 0.4));
  }
  50%, 100% { 
    box-shadow: 0 0 5px var(--glow-color, rgba(99, 102, 241, 0.3));
  }
`

export const bounceLoop = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`

export const bounceLoopWithPause = keyframes`
  0% { transform: translateY(0); }
  25% { transform: translateY(-6px); }
  50%, 100% { transform: translateY(0); }
`

// Peaking effect - supports direction via CSS variables
// --peak-offset: how far to peek (e.g., '80%' or '-80%' for opposite direction)
export const peakingIdle = keyframes`
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(var(--peak-offset, 80%)); }
`

export const peakingIdleWithPause = keyframes`
  0% { transform: translateX(0); }
  25% { transform: translateX(var(--peak-offset, 80%)); }
  50%, 100% { transform: translateX(0); }
`

// Vertical peaking for up/down directions
export const peakingIdleVertical = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(var(--peak-offset, 80%)); }
`

export const peakingIdleVerticalWithPause = keyframes`
  0% { transform: translateY(0); }
  25% { transform: translateY(var(--peak-offset, 80%)); }
  50%, 100% { transform: translateY(0); }
`

export const pulseLoop = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`

export const pulseLoopWithPause = keyframes`
  0% { transform: scale(1); }
  25% { transform: scale(1.1); }
  50%, 100% { transform: scale(1); }
`

export const shakeLoop = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-3px); }
  40% { transform: translateX(3px); }
  60% { transform: translateX(-3px); }
  80% { transform: translateX(3px); }
`

export const shakeLoopWithPause = keyframes`
  0% { transform: translateX(0); }
  10% { transform: translateX(-3px); }
  20% { transform: translateX(3px); }
  30% { transform: translateX(-3px); }
  40% { transform: translateX(3px); }
  50%, 100% { transform: translateX(0); }
`

export const wobbleLoop = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-5deg); }
  75% { transform: rotate(5deg); }
`

export const wobbleLoopWithPause = keyframes`
  0% { transform: rotate(0deg); }
  12.5% { transform: rotate(-5deg); }
  37.5% { transform: rotate(5deg); }
  50%, 100% { transform: rotate(0deg); }
`

export const spinLoop = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

export const spinLoopWithPause = keyframes`
  0% { transform: rotate(0deg); }
  50% { transform: rotate(360deg); }
  100% { transform: rotate(360deg); }
`

export const breatheLoop = keyframes`
  0%, 100% { 
    transform: scale(1); 
    opacity: 1;
  }
  50% { 
    transform: scale(1.05); 
    opacity: 0.8;
  }
`

export const breatheLoopWithPause = keyframes`
  0% { 
    transform: scale(1); 
    opacity: 1;
  }
  25% { 
    transform: scale(1.05); 
    opacity: 0.8;
  }
  50%, 100% { 
    transform: scale(1); 
    opacity: 1;
  }
`

// ═══════════════════════════════════════════════════════════════════════════
// DEFAULT CONFIGURATIONS
// Organized by component and effect type
// ═══════════════════════════════════════════════════════════════════════════

export interface AnimationDefaults {
  intro: {
    animation: ReturnType<typeof keyframes>
    duration: number
    easing: string
  }
  exit: {
    animation: ReturnType<typeof keyframes>
    duration: number
    easing: string
  }
}

export const WIDGET_ICON_DEFAULTS: AnimationDefaults = {
  intro: {
    animation: fadeIn,
    duration: 300,
    easing: 'ease-out',
  },
  exit: {
    animation: fadeOut,
    duration: 200,
    easing: 'ease-in',
  },
}

export const WELCOME_MESSAGE_DEFAULTS: AnimationDefaults = {
  intro: {
    animation: fadeIn,
    duration: 300,
    easing: 'ease-out',
  },
  exit: {
    animation: fadeOut,
    duration: 200,
    easing: 'ease-in',
  },
}

// Base defaults shared across all attention effects
export const ATTENTION_BASE_DEFAULTS = {
  delay: 0,
  repeat: true,
  repeatCount: 0, // 0 = infinite
  repeatDelay: 0, // 0 = no pause between iterations
}

export const ATTENTION_DEFAULTS = {
  glowEffect: {
    ...ATTENTION_BASE_DEFAULTS,
    duration: 2000,
    color: 'rgba(99, 102, 241, 0.8)',
  },
  bounceEffect: {
    ...ATTENTION_BASE_DEFAULTS,
    duration: 1000,
  },
  peakingEffect: {
    ...ATTENTION_BASE_DEFAULTS,
    duration: 3000,
    offset: '80%',
    direction: 'right' as const, // 'left' | 'right' | 'up' | 'down'
  },
  pulseEffect: {
    ...ATTENTION_BASE_DEFAULTS,
    duration: 1500,
  },
  shakeEffect: {
    ...ATTENTION_BASE_DEFAULTS,
    duration: 500,
  },
  wobbleEffect: {
    ...ATTENTION_BASE_DEFAULTS,
    duration: 1000,
  },
  spinEffect: {
    ...ATTENTION_BASE_DEFAULTS,
    duration: 3000,
  },
  breatheEffect: {
    ...ATTENTION_BASE_DEFAULTS,
    duration: 2000,
  },
}

export type TextEffectType =
  | 'typewriter'
  | 'none'
  | 'fade-words'
  | 'fade-lines'
  | 'slide-words'
  | 'scramble'
  | 'highlight'
  | 'bounce-letters'
  | 'wave'
  | 'glitch'

export const TEXT_EFFECT_DEFAULTS: Record<TextEffectType, { enabled: boolean; speed: number; delay: number }> =
  {
    typewriter: { enabled: true, speed: 40, delay: 0 },
    none: { enabled: false, speed: 0, delay: 0 },
    'fade-words': { enabled: true, speed: 150, delay: 0 },
    'fade-lines': { enabled: true, speed: 300, delay: 0 },
    'slide-words': { enabled: true, speed: 100, delay: 0 },
    scramble: { enabled: true, speed: 30, delay: 0 },
    highlight: { enabled: true, speed: 50, delay: 0 },
    'bounce-letters': { enabled: true, speed: 50, delay: 0 },
    wave: { enabled: true, speed: 80, delay: 0 },
    glitch: { enabled: true, speed: 50, delay: 0 },
  }

/**
 * @deprecated Use TEXT_EFFECT_DEFAULTS['typewriter'] instead
 */
export const TYPEWRITER_DEFAULTS = TEXT_EFFECT_DEFAULTS.typewriter

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

export function getAnimationProps(effect?: Effect) {
  const duration = effect?.duration ?? 300
  const delay = effect?.delay ?? 0
  const repeat = effect?.repeat ?? false
  const repeatCount = effect?.repeatCount ?? (repeat ? 'infinite' : 1)
  const fillMode = repeat ? 'none' : 'forwards'

  return { duration, delay, repeat, repeatCount, fillMode }
}

function getSlideInKeyframe(direction: string) {
  switch (direction) {
    case 'right':
      return slideInRight
    case 'up':
      return slideInUp
    case 'down':
      return slideInDown
    default:
      return slideInLeft
  }
}

function getSlideOutKeyframe(direction: string) {
  switch (direction) {
    case 'right':
      return slideOutRight
    case 'up':
      return slideOutUp
    case 'down':
      return slideOutDown
    default:
      return slideOutLeft
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// INTRO/EXIT ANIMATION GENERATORS
// ═══════════════════════════════════════════════════════════════════════════

export function getIntroAnimation(effect?: Effect, defaults: AnimationDefaults = WELCOME_MESSAGE_DEFAULTS) {
  if (!effect?.enabled) {
    const { animation, duration, easing } = defaults.intro
    return css`animation: ${animation} ${duration}ms ${easing} forwards;`
  }

  const { duration, delay, repeatCount, fillMode } = getAnimationProps(effect)
  const direction = effect.direction ?? 'left'

  switch (effect.effect) {
    case 'slide': {
      const slideAnim = getSlideInKeyframe(direction)
      return css`animation: ${slideAnim} ${duration}ms ease-out ${delay}ms ${repeatCount} ${fillMode};`
    }
    case 'scale':
      return css`animation: ${scaleIn} ${duration}ms ease-out ${delay}ms ${repeatCount} ${fillMode};`
    case 'bounce':
      return css`animation: ${bounceIn} ${duration}ms ease-out ${delay}ms ${repeatCount} ${fillMode};`
    case 'flip':
      return css`animation: ${flipIn} ${duration}ms ease-out ${delay}ms ${repeatCount} ${fillMode};`
    case 'rotate':
      return css`animation: ${rotateIn} ${duration}ms ease-out ${delay}ms ${repeatCount} ${fillMode};`
    case 'blur':
      return css`animation: ${blurIn} ${duration}ms ease-out ${delay}ms ${repeatCount} ${fillMode};`
    case 'elastic':
      return css`animation: ${elasticIn} ${duration}ms ease-out ${delay}ms ${repeatCount} ${fillMode};`
    case 'drop':
      return css`animation: ${dropIn} ${duration}ms ease-out ${delay}ms ${repeatCount} ${fillMode};`
    case 'fade':
    default:
      return css`animation: ${fadeIn} ${duration}ms ease-out ${delay}ms ${repeatCount} ${fillMode};`
  }
}

export function getExitAnimation(effect?: Effect, defaults: AnimationDefaults = WELCOME_MESSAGE_DEFAULTS) {
  if (!effect?.enabled) {
    const { animation, duration, easing } = defaults.exit
    return css`animation: ${animation} ${duration}ms ${easing} forwards;`
  }

  const { duration, delay } = getAnimationProps(effect)
  const direction = effect.direction ?? 'left'

  switch (effect.effect) {
    case 'slide': {
      const slideAnim = getSlideOutKeyframe(direction)
      return css`animation: ${slideAnim} ${duration}ms ease-in ${delay}ms forwards;`
    }
    case 'scale':
      return css`animation: ${scaleOut} ${duration}ms ease-in ${delay}ms forwards;`
    case 'bounce':
      return css`animation: ${bounceOut} ${duration}ms ease-in ${delay}ms forwards;`
    case 'flip':
      return css`animation: ${flipOut} ${duration}ms ease-in ${delay}ms forwards;`
    case 'rotate':
      return css`animation: ${rotateOut} ${duration}ms ease-in ${delay}ms forwards;`
    case 'blur':
      return css`animation: ${blurOut} ${duration}ms ease-in ${delay}ms forwards;`
    case 'elastic':
      return css`animation: ${elasticOut} ${duration}ms ease-in ${delay}ms forwards;`
    case 'drop':
      return css`animation: ${dropOut} ${duration}ms ease-in ${delay}ms forwards;`
    case 'fade':
    default:
      return css`animation: ${fadeOut} ${duration}ms ease-in ${delay}ms forwards;`
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// ATTENTION ANIMATION GENERATOR
//
// Supports all Effect properties:
// - enabled: whether the effect is active
// - duration: how long one animation cycle takes
// - delay: time before animation starts
// - repeat: whether to loop (true) or play once (false)
// - repeatCount: number of iterations (0 = infinite, only used if repeat=true)
// - repeatDelay: pause between iterations (uses "WithPause" keyframes)
// - color: for glowEffect
// - direction: for peakingEffect ('left' | 'right' | 'up' | 'down')
// ═══════════════════════════════════════════════════════════════════════════

export interface AttentionEffects {
  glowEffect?: Effect
  bounceEffect?: Effect
  peakingEffect?: Effect
  pulseEffect?: Effect
  shakeEffect?: Effect
  wobbleEffect?: Effect
  spinEffect?: Effect
  breatheEffect?: Effect
}

interface AttentionAnimConfig {
  keyframe: ReturnType<typeof keyframes>
  keyframeWithPause: ReturnType<typeof keyframes>
  duration: number
  delay: number
  repeat: boolean
  repeatCount: number
  repeatDelay: number
  easing: string
}

interface AttentionAnimResult {
  keyframe: ReturnType<typeof keyframes>
  params: string
}

/**
 * Builds animation params from an attention effect configuration.
 * Returns the keyframe separately so it can be properly interpolated by styled-components.
 * Handles repeatDelay by using "WithPause" keyframes and adjusting duration.
 */
function buildAttentionAnimParams(config: AttentionAnimConfig): AttentionAnimResult {
  const { keyframe, keyframeWithPause, duration, delay, repeat, repeatCount, repeatDelay, easing } = config

  // Determine which keyframe to use and calculate total duration
  const hasRepeatDelay = repeatDelay > 0
  const selectedKeyframe = hasRepeatDelay ? keyframeWithPause : keyframe
  // With pause keyframes, animation is in first 50%, so add repeatDelay to duration
  const totalDuration = hasRepeatDelay ? duration + repeatDelay : duration

  // Determine iteration count
  let iterationCount: string | number = 'infinite'
  if (!repeat) {
    iterationCount = 1
  } else if (repeatCount > 0) {
    iterationCount = repeatCount
  }

  // Build animation params string - delay is in ms
  const delayStr = delay > 0 ? `${delay}ms` : '0ms'

  return {
    keyframe: selectedKeyframe,
    params: `${totalDuration}ms ${easing} ${delayStr} ${iterationCount}`,
  }
}

/**
 * Gets the peak offset value based on direction
 */
function getPeakOffset(direction: string, offset: string): string {
  switch (direction) {
    case 'left':
      return `-${offset}`
    case 'up':
      return `-${offset}`
    case 'down':
      return offset
    case 'right':
    default:
      return offset
  }
}

export function getAttentionAnimation(effects?: AttentionEffects) {
  if (!effects) return css``

  const hasGlow = effects.glowEffect?.enabled
  const hasBounce = effects.bounceEffect?.enabled
  const hasPeaking = effects.peakingEffect?.enabled
  const hasPulse = effects.pulseEffect?.enabled
  const hasShake = effects.shakeEffect?.enabled
  const hasWobble = effects.wobbleEffect?.enabled
  const hasSpin = effects.spinEffect?.enabled
  const hasBreathe = effects.breatheEffect?.enabled

  const transformEffects = [hasBounce, hasPeaking, hasPulse, hasShake, hasWobble, hasSpin, hasBreathe].filter(
    Boolean
  )
  const hasAnyTransform = transformEffects.length > 0

  if (!hasGlow && !hasAnyTransform) return css``

  // Build glow animation config
  const glowConfig: AttentionAnimConfig | null = hasGlow
    ? {
        keyframe: glowPulse,
        keyframeWithPause: glowPulseWithPause,
        duration: effects.glowEffect?.duration ?? ATTENTION_DEFAULTS.glowEffect.duration,
        delay: effects.glowEffect?.delay ?? ATTENTION_DEFAULTS.glowEffect.delay,
        repeat: effects.glowEffect?.repeat ?? ATTENTION_DEFAULTS.glowEffect.repeat,
        repeatCount: effects.glowEffect?.repeatCount ?? ATTENTION_DEFAULTS.glowEffect.repeatCount,
        repeatDelay: effects.glowEffect?.repeatDelay ?? ATTENTION_DEFAULTS.glowEffect.repeatDelay,
        easing: 'ease-in-out',
      }
    : null

  const glowColor = effects.glowEffect?.color ?? ATTENTION_DEFAULTS.glowEffect.color

  // Build transform animation config (only one can be active)
  const getTransformConfig = (): AttentionAnimConfig | null => {
    if (hasBounce) {
      const e = effects.bounceEffect!
      return {
        keyframe: bounceLoop,
        keyframeWithPause: bounceLoopWithPause,
        duration: e.duration ?? ATTENTION_DEFAULTS.bounceEffect.duration,
        delay: e.delay ?? ATTENTION_DEFAULTS.bounceEffect.delay,
        repeat: e.repeat ?? ATTENTION_DEFAULTS.bounceEffect.repeat,
        repeatCount: e.repeatCount ?? ATTENTION_DEFAULTS.bounceEffect.repeatCount,
        repeatDelay: e.repeatDelay ?? ATTENTION_DEFAULTS.bounceEffect.repeatDelay,
        easing: 'ease-in-out',
      }
    }
    if (hasPulse) {
      const e = effects.pulseEffect!
      return {
        keyframe: pulseLoop,
        keyframeWithPause: pulseLoopWithPause,
        duration: e.duration ?? ATTENTION_DEFAULTS.pulseEffect.duration,
        delay: e.delay ?? ATTENTION_DEFAULTS.pulseEffect.delay,
        repeat: e.repeat ?? ATTENTION_DEFAULTS.pulseEffect.repeat,
        repeatCount: e.repeatCount ?? ATTENTION_DEFAULTS.pulseEffect.repeatCount,
        repeatDelay: e.repeatDelay ?? ATTENTION_DEFAULTS.pulseEffect.repeatDelay,
        easing: 'ease-in-out',
      }
    }
    if (hasShake) {
      const e = effects.shakeEffect!
      return {
        keyframe: shakeLoop,
        keyframeWithPause: shakeLoopWithPause,
        duration: e.duration ?? ATTENTION_DEFAULTS.shakeEffect.duration,
        delay: e.delay ?? ATTENTION_DEFAULTS.shakeEffect.delay,
        repeat: e.repeat ?? ATTENTION_DEFAULTS.shakeEffect.repeat,
        repeatCount: e.repeatCount ?? ATTENTION_DEFAULTS.shakeEffect.repeatCount,
        repeatDelay: e.repeatDelay ?? ATTENTION_DEFAULTS.shakeEffect.repeatDelay,
        easing: 'ease-in-out',
      }
    }
    if (hasWobble) {
      const e = effects.wobbleEffect!
      return {
        keyframe: wobbleLoop,
        keyframeWithPause: wobbleLoopWithPause,
        duration: e.duration ?? ATTENTION_DEFAULTS.wobbleEffect.duration,
        delay: e.delay ?? ATTENTION_DEFAULTS.wobbleEffect.delay,
        repeat: e.repeat ?? ATTENTION_DEFAULTS.wobbleEffect.repeat,
        repeatCount: e.repeatCount ?? ATTENTION_DEFAULTS.wobbleEffect.repeatCount,
        repeatDelay: e.repeatDelay ?? ATTENTION_DEFAULTS.wobbleEffect.repeatDelay,
        easing: 'ease-in-out',
      }
    }
    if (hasSpin) {
      const e = effects.spinEffect!
      return {
        keyframe: spinLoop,
        keyframeWithPause: spinLoopWithPause,
        duration: e.duration ?? ATTENTION_DEFAULTS.spinEffect.duration,
        delay: e.delay ?? ATTENTION_DEFAULTS.spinEffect.delay,
        repeat: e.repeat ?? ATTENTION_DEFAULTS.spinEffect.repeat,
        repeatCount: e.repeatCount ?? ATTENTION_DEFAULTS.spinEffect.repeatCount,
        repeatDelay: e.repeatDelay ?? ATTENTION_DEFAULTS.spinEffect.repeatDelay,
        easing: 'linear',
      }
    }
    if (hasBreathe) {
      const e = effects.breatheEffect!
      return {
        keyframe: breatheLoop,
        keyframeWithPause: breatheLoopWithPause,
        duration: e.duration ?? ATTENTION_DEFAULTS.breatheEffect.duration,
        delay: e.delay ?? ATTENTION_DEFAULTS.breatheEffect.delay,
        repeat: e.repeat ?? ATTENTION_DEFAULTS.breatheEffect.repeat,
        repeatCount: e.repeatCount ?? ATTENTION_DEFAULTS.breatheEffect.repeatCount,
        repeatDelay: e.repeatDelay ?? ATTENTION_DEFAULTS.breatheEffect.repeatDelay,
        easing: 'ease-in-out',
      }
    }
    if (hasPeaking) {
      const e = effects.peakingEffect!
      const direction = e.direction ?? ATTENTION_DEFAULTS.peakingEffect.direction
      const isVertical = direction === 'up' || direction === 'down'
      return {
        keyframe: isVertical ? peakingIdleVertical : peakingIdle,
        keyframeWithPause: isVertical ? peakingIdleVerticalWithPause : peakingIdleWithPause,
        duration: e.duration ?? ATTENTION_DEFAULTS.peakingEffect.duration,
        delay: e.delay ?? ATTENTION_DEFAULTS.peakingEffect.delay,
        repeat: e.repeat ?? ATTENTION_DEFAULTS.peakingEffect.repeat,
        repeatCount: e.repeatCount ?? ATTENTION_DEFAULTS.peakingEffect.repeatCount,
        repeatDelay: e.repeatDelay ?? ATTENTION_DEFAULTS.peakingEffect.repeatDelay,
        easing: 'ease-in-out',
      }
    }
    return null
  }

  const transformConfig = getTransformConfig()

  // Build CSS variables for effects that need them
  const peakDirection = effects.peakingEffect?.direction ?? ATTENTION_DEFAULTS.peakingEffect.direction
  const peakOffsetValue = getPeakOffset(peakDirection, ATTENTION_DEFAULTS.peakingEffect.offset)

  // Combine animations
  if (hasGlow && glowConfig && transformConfig) {
    const glow = buildAttentionAnimParams(glowConfig)
    const transform = buildAttentionAnimParams(transformConfig)
    return css`
      --glow-color: ${glowColor};
      --peak-offset: ${peakOffsetValue};
      animation: ${glow.keyframe} ${glow.params}, ${transform.keyframe} ${transform.params};
    `
  }

  if (hasGlow && glowConfig) {
    const glow = buildAttentionAnimParams(glowConfig)
    return css`
      --glow-color: ${glowColor};
      animation: ${glow.keyframe} ${glow.params};
    `
  }

  if (transformConfig) {
    const transform = buildAttentionAnimParams(transformConfig)
    return css`
      --peak-offset: ${peakOffsetValue};
      animation: ${transform.keyframe} ${transform.params};
    `
  }

  return css``
}

// ═══════════════════════════════════════════════════════════════════════════
// TEXT EFFECT HELPERS
// ═══════════════════════════════════════════════════════════════════════════

export function getTextEffectType(effect?: Effect): TextEffectType {
  if (!effect?.enabled) return 'none'
  const effectType = effect.effect as TextEffectType
  const validTypes: TextEffectType[] = [
    'typewriter',
    'none',
    'fade-words',
    'fade-lines',
    'slide-words',
    'scramble',
    'highlight',
    'bounce-letters',
    'wave',
    'glitch',
  ]
  if (validTypes.includes(effectType)) {
    return effectType
  }
  return 'typewriter'
}
